import React from 'react';

export const COMPANIES_API_ITEMS = [
  'List Companies',
  'Create Company',
  'Get Company',
  'Update Company',
  'Delete Company',
];

export const PIPELINES_API_ITEMS = [
  'List Pipelines',
  'Get Pipeline',
  'Create Pipeline',
  'Update Pipeline',
  'Delete Pipeline',
  'List Pipeline Items',
  'Get Pipeline Item',
  'Create Pipeline Item',
  'Update Pipeline Item',
  'Delete Pipeline Item',
];

/** @type {Record<string, string>} */
export const COMPANIES_API_PATHS = {
  'List Companies': '/companies',
  'Create Company': '/companies',
  'Get Company': '/companies/{company_id}',
  'Update Company': '/companies/{company_id}',
  'Delete Company': '/companies/{company_id}',
};

/** @type {Record<string, string>} */
export const PIPELINES_API_PATHS = {
  'List Pipelines': '/pipelines',
  'Get Pipeline': '/pipelines/{pipeline_id}',
  'Create Pipeline': '/pipelines',
  'Update Pipeline': '/pipelines/{pipeline_id}',
  'Delete Pipeline': '/pipelines/{pipeline_id}',
  'List Pipeline Items': '/pipelines/{pipeline_id}/items',
  'Get Pipeline Item': '/pipelines/{pipeline_id}/items/{item_id}',
  'Create Pipeline Item': '/pipelines/{pipeline_id}/items',
  'Update Pipeline Item': '/pipelines/{pipeline_id}/items/{item_id}',
  'Delete Pipeline Item': '/pipelines/{pipeline_id}/items/{item_id}',
};

const SAMPLE_KEY = '[YOUR API KEY]...Vw00sc4rlg8';

function curlExample(method, path, body) {
  const lines = [`curl -X ${method} "\${baseUrl}${path}" \\`, `  -H "Authorization: Bearer ${SAMPLE_KEY}"`];
  if (body) {
    lines.push('  -H "Content-Type: application/json" \\');
    lines.push(`  -d '${body}'`);
  }
  return lines.join('\n');
}

function simplePage({ title, description, method, path, body, response }) {
  const cards = [
    {
      title: 'Example request',
      type: 'curl',
      content: curlExample(method, path, body),
    },
  ];
  if (response) {
    cards.push({ title: 'Example response', type: 'response', content: response });
  }
  return {
    title,
    content: (
      <div>
        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
          {description}
        </p>
      </div>
    ),
    cards,
  };
}

/**
 * @param {string} pageName
 * @param {() => string} getApiBaseUrl
 */
export function buildCompaniesPipelinesPageContent(pageName, getApiBaseUrl) {
  const baseUrl = getApiBaseUrl();

  const pages = {
    'List Companies': simplePage({
      title: 'List Companies',
      description:
        'Retrieve companies in the workspace with pagination, search, and filters. Archived companies are excluded.',
      method: 'GET',
      path: '/companies?page=1&limit=20',
    }),
    'Create Company': simplePage({
      title: 'Create Company',
      description: 'Create a company. Requires API key write permission.',
      method: 'POST',
      path: '/companies',
      body: '{"name":"Acme Corp","industry":"Software","website":"https://acme.example"}',
      response: '{ "data": { "id": "...", "name": "Acme Corp" }, "message": "Company created successfully" }',
    }),
    'Get Company': simplePage({
      title: 'Get Company',
      description: 'Get one company by ID. Returns 404 if the company was deleted or archived.',
      method: 'GET',
      path: '/companies/123e4567-e89b-12d3-a456-426614174000',
    }),
    'Update Company': simplePage({
      title: 'Update Company',
      description: 'Update company fields. Send only fields you want to change.',
      method: 'PUT',
      path: '/companies/123e4567-e89b-12d3-a456-426614174000',
      body: '{"name":"Acme Corp (Updated)","status":"Active"}',
    }),
    'Delete Company': simplePage({
      title: 'Delete Company',
      description:
        'Permanently deletes the company (same as the app UI). Linked contacts are unlinked first. Subsequent GET by ID returns 404.',
      method: 'DELETE',
      path: '/companies/123e4567-e89b-12d3-a456-426614174000',
      response: '{ "message": "Company deleted successfully", "data": { "id": "...", "name": "Acme Corp" } }',
    }),
    'List Pipelines': simplePage({
      title: 'List Pipelines',
      description: 'List pipelines in the workspace with pagination and filters (type, archived, slug).',
      method: 'GET',
      path: '/pipelines?page=1&limit=20',
    }),
    'Get Pipeline': simplePage({
      title: 'Get Pipeline',
      description: 'Get one pipeline by ID, including stages and settings.',
      method: 'GET',
      path: '/pipelines/123e4567-e89b-12d3-a456-426614174000',
    }),
    'Create Pipeline': simplePage({
      title: 'Create Pipeline',
      description:
        'Create a pipeline. Requires name, pipeline_type (contacts | companies | deals | general), and at least one stage object { id, name, color }.',
      method: 'POST',
      path: '/pipelines',
      body:
        '{"name":"Sales Board","pipeline_type":"deals","stages":[{"id":"stage-1","name":"New","color":"#10b981"}]}',
    }),
    'Update Pipeline': simplePage({
      title: 'Update Pipeline',
      description: 'Update pipeline name, description, stages, settings_json, or slug.',
      method: 'PUT',
      path: '/pipelines/123e4567-e89b-12d3-a456-426614174000',
      body: '{"name":"Sales Board (Q3)"}',
    }),
    'Delete Pipeline': simplePage({
      title: 'Delete Pipeline',
      description:
        'Permanently deletes the pipeline and all of its items (cards). Matches the app Danger zone delete pipeline action.',
      method: 'DELETE',
      path: '/pipelines/123e4567-e89b-12d3-a456-426614174000',
      response: '{ "message": "Pipeline deleted successfully", "data": { "id": "...", "name": "Sales Board" } }',
    }),
    'List Pipeline Items': simplePage({
      title: 'List Pipeline Items',
      description: 'List cards in a pipeline. Optional query: stage_id to filter by stage.',
      method: 'GET',
      path: '/pipelines/123e4567-e89b-12d3-a456-426614174000/items?page=1&limit=50',
    }),
    'Get Pipeline Item': simplePage({
      title: 'Get Pipeline Item',
      description: 'Get one pipeline item (card) by pipeline ID and item ID.',
      method: 'GET',
      path: '/pipelines/123e4567-e89b-12d3-a456-426614174000/items/aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee',
    }),
    'Create Pipeline Item': simplePage({
      title: 'Create Pipeline Item',
      description: 'Add a card to a pipeline. Requires title and stage_id.',
      method: 'POST',
      path: '/pipelines/123e4567-e89b-12d3-a456-426614174000/items',
      body: '{"title":"New lead","stage_id":"stage-1","item_type":"contact"}',
    }),
    'Update Pipeline Item': simplePage({
      title: 'Update Pipeline Item',
      description: 'Update a pipeline card (title, stage, assignees, custom fields, etc.).',
      method: 'PUT',
      path: '/pipelines/123e4567-e89b-12d3-a456-426614174000/items/aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee',
      body: '{"title":"Updated lead","status":"In Progress"}',
    }),
    'Delete Pipeline Item': simplePage({
      title: 'Delete Pipeline Item',
      description: 'Permanently deletes one pipeline card.',
      method: 'DELETE',
      path: '/pipelines/123e4567-e89b-12d3-a456-426614174000/items/aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee',
      response:
        '{ "message": "Pipeline item deleted successfully", "data": { "id": "...", "title": "New lead" } }',
    }),
  };

  const page = pages[pageName];
  if (!page) return null;

  // Substitute baseUrl in curl examples
  return {
    ...page,
    cards: page.cards.map((card) =>
      card.type === 'curl'
        ? { ...card, content: card.content.replace(/\$\{baseUrl\}/g, baseUrl) }
        : card
    ),
  };
}

/** HTTP method pill for sidebar */
export function getCompaniesPipelinesMethodPill(item) {
  if (item.startsWith('List ') || item.startsWith('Get ')) return ['#10B981', 'GET'];
  if (item.startsWith('Create ')) return ['#3B82F6', 'POST'];
  if (item.startsWith('Update ')) return ['#F59E0B', 'PUT'];
  if (item.startsWith('Delete ')) return ['#EF4444', 'DELETE'];
  return null;
}

/** Slug for /api-docs/{section}/{page} */
export function getCompaniesPipelinesUrlSlug(pageName) {
  const map = {
    'List Companies': '/api-docs/companies/list-companies',
    'Create Company': '/api-docs/companies/create-company',
    'Get Company': '/api-docs/companies/get-company',
    'Update Company': '/api-docs/companies/update-company',
    'Delete Company': '/api-docs/companies/delete-company',
    'List Pipelines': '/api-docs/pipelines/list-pipelines',
    'Get Pipeline': '/api-docs/pipelines/get-pipeline',
    'Create Pipeline': '/api-docs/pipelines/create-pipeline',
    'Update Pipeline': '/api-docs/pipelines/update-pipeline',
    'Delete Pipeline': '/api-docs/pipelines/delete-pipeline',
    'List Pipeline Items': '/api-docs/pipelines/list-pipeline-items',
    'Get Pipeline Item': '/api-docs/pipelines/get-pipeline-item',
    'Create Pipeline Item': '/api-docs/pipelines/create-pipeline-item',
    'Update Pipeline Item': '/api-docs/pipelines/update-pipeline-item',
    'Delete Pipeline Item': '/api-docs/pipelines/delete-pipeline-item',
  };
  return map[pageName] || null;
}

/** Reverse map URL page slug → display name */
export function getCompaniesPipelinesPageFromUrl(section, page) {
  if (section === 'companies') {
    if (page === 'list-companies') return 'List Companies';
    if (page === 'create-company') return 'Create Company';
    if (page === 'get-company') return 'Get Company';
    if (page === 'update-company') return 'Update Company';
    if (page === 'delete-company') return 'Delete Company';
    return 'List Companies';
  }
  if (section === 'pipelines') {
    if (page === 'list-pipelines') return 'List Pipelines';
    if (page === 'get-pipeline') return 'Get Pipeline';
    if (page === 'create-pipeline') return 'Create Pipeline';
    if (page === 'update-pipeline') return 'Update Pipeline';
    if (page === 'delete-pipeline') return 'Delete Pipeline';
    if (page === 'list-pipeline-items') return 'List Pipeline Items';
    if (page === 'get-pipeline-item') return 'Get Pipeline Item';
    if (page === 'create-pipeline-item') return 'Create Pipeline Item';
    if (page === 'update-pipeline-item') return 'Update Pipeline Item';
    if (page === 'delete-pipeline-item') return 'Delete Pipeline Item';
    return 'List Pipelines';
  }
  return null;
}
